import { formatDate} from '../../utils/formatters.js';

export const standardPDFStyles = {
    table: {
        header: {
            default: {
                fillColor: [230, 230, 230],
                textColor: [0, 0, 0],
                lineColor: [0, 0, 0],
                lineWidth: 0.5
            }
        },
        body: {
            default: {
                lineColor: [0, 0, 0],
                lineWidth: 0.5
            },
            associated_liability: {
                fontStyle: 'italic',
                textColor: [231, 76, 60], // Red
                lineColor: [0, 0, 0],
                lineWidth: 0.5
            },
            net_equity: {
                fontStyle: 'bolditalic',
                fillColor: [240, 248, 255], // Light Blue
                lineColor: [0, 0, 0],
                lineWidth: 0.5
            },
            category_total: {
                fontStyle: 'bold',
                fillColor: [249, 249, 249], // Light Gray
                lineColor: [0, 0, 0],
                lineWidth: 0.5
            },
            asset_liability_total: {
                fontStyle: 'bold',
                fillColor: [249, 249, 249], // Light Gray
                lineColor: [0, 0, 0],
                lineWidth: 0.5
            },
            transfer_to: {
                fontStyle: 'italic',
                fillColor: [240, 248, 255], // Light Blue
                lineColor: [0, 0, 0],
                lineWidth: 0.5
            },
            index_column: {
                fontStyle: 'bold'
            }
        }
    },
};

export function normalizeMargin(margin, defaults = { top: 20, right: 20, bottom: 20, left: 20 }) {
    // If margin is a number, use it for all sides
    if (typeof margin === 'number') {
        return {
            top: margin,
            right: margin,
            bottom: margin,
            left: margin
        };
    }

    // If margin is an array [top, right, bottom, left]
    if (Array.isArray(margin)) {
        const [top, right, bottom, left] = margin;
        return {
            top: top ?? defaults.top,
            right: right ?? defaults.right,
            bottom: bottom ?? defaults.bottom,
            left: left ?? defaults.left
        };
    }

    // If margin is an object, merge with defaults
    if (typeof margin === 'object' && margin !== null) {
        return {
            top: margin.top ?? defaults.top,
            right: margin.right ?? defaults.right,
            bottom: margin.bottom ?? defaults.bottom,
            left: margin.left ?? defaults.left
        };
    }

    // If margin is undefined/null or invalid, return defaults
    return { ...defaults };
}

export function addFootersToAllPages(doc, currentOrg, displayOptions, margin, footerMargin = 30) {
    margin = normalizeMargin(margin);

    // Function to add footer to current page
    const addFooter = (pageNum = null, totalPages = null) => {
        const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
        const pageHeight = doc.internal.pageSize.height;
        const pageWidth = doc.internal.pageSize.width;
        const finalY = pageHeight - footerMargin; // Position footer near bottom of page

        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');

        // Left footer - date/time
        doc.text(`${formatDate(new Date())}\n${new Date().toLocaleTimeString()}`, margin.left, finalY);

        // Center footer - organization, draft status, and copyright
        let centerText = '';
        if (currentOrg.orgName) {
            centerText += `Prepared by ${currentOrg.orgName}\n`;
        }
        if (displayOptions.isDraft) {
            centerText += 'DRAFT - for settlement purposes only\n';
        }
        centerText += `© ${new Date().getFullYear()} Livisibly LLC All rights reserved.`;

        doc.text(centerText, pageWidth / 2, finalY, { align: 'center' });

        // Right footer - page number
        const pageText = totalPages ? `Page ${pageNum || currentPage} of ${totalPages}` : `Page ${currentPage}`;
        doc.text(pageText, pageWidth - margin.right, finalY, { align: 'right' });
    };

    const pageCount = doc.internal.getNumberOfPages();

    // Store current page to restore later
    const currentPage = doc.internal.getCurrentPageInfo().pageNumber;

    // Loop through all pages
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        addFooter(i, pageCount);
    }

    // Restore to the original page
    doc.setPage(currentPage);
}

export function addPageIfNeeded(doc, margin, yPosition, yThresholdFactor=0.6667) {
    const pageHeight = doc.internal.pageSize.getHeight()
    const newPageThreshold = pageHeight * yThresholdFactor;

    if (yPosition > newPageThreshold) {
        doc.addPage();
        yPosition = normalizeMargin(margin).top;
    }
    return yPosition;
}

// Replace characters jsPDF's default Helvetica encoding can't render
// reliably with ASCII equivalents, then strip anything still outside the
// printable-ASCII range. Unicode chars like the U+2192 arrow (→) used by
// DependentMultiSelectPairFilter's description trigger broken width
// calculations in splitTextToSize, which manifests as letter-spacing
// artifacts and chunks of text disappearing mid-line in the rendered PDF.
// Exported so report modules can sanitize free-text they put into tables
// (account names, document names) — autoTable bodies are not sanitized
// anywhere else.
export function sanitizeForHelvetica(text) {
    if (text === undefined || text === null) return '';
    return String(text)
        .replace(/[→➜➡➔]/g, '->')   // various right arrows
        .replace(/[←]/g, '<-')                     // left arrows
        .replace(/[–—]/g, '-')                // en/em dashes
        .replace(/[‘’]/g, "'")                // smart single quotes
        .replace(/[“”]/g, '"')                // smart double quotes
        .replace(/[…]/g, '...')                    // ellipsis
        .replace(/[ ]/g, ' ')                      // nbsp
        .replace(/[^\x20-\x7E]/g, '');                  // any remaining non-ASCII
}

export function addFiltersDescription(doc, filtersDescription, margin, yPosition) {
    return addCenteredDescriptionBlock(doc, 'Filters Applied:', filtersDescription, margin, yPosition);
}

/**
 * Draws a centered "<header>" line followed by one centered line per entry,
 * wrapping long entries inside the page margins and paging when a line would
 * overflow. Shared by the Filters Applied and Sort Order blocks so a report's
 * provenance captions stay visually identical.
 */
export function addCenteredDescriptionBlock(doc, header, lines, margin, yPosition) {
    margin = normalizeMargin(margin)
    if (!lines || lines.length === 0) return yPosition;

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const effectivePageWidth = pageWidth - margin.left - margin.right;

    const fontSize = 10;
    // jsPDF default line height factor is ~1.15. Using 12pt per line for
    // 10pt font keeps wrapped lines from overlapping each other and from
    // colliding with the next section (the table).
    const lineHeight = 12;

    doc.setFontSize(fontSize);
    doc.setFont(undefined, 'normal');

    // Each line is rendered horizontally centered on the page, matching the
    // title block above (which uses pageWidth / 2 + align:'center'). Page-
    // breaks before drawing if the next line wouldn't fit.
    const centerX = pageWidth / 2;
    const drawCenteredLine = (line) => {
        if (yPosition + lineHeight > pageHeight - margin.bottom) {
            doc.addPage();
            yPosition = margin.top;
        }
        doc.text(line, centerX, yPosition, { align: 'center', baseline: 'top' });
        yPosition += lineHeight;
    };

    // Header
    drawCenteredLine(header);

    // Each entry on its own line, sanitized for Helvetica and wrapped
    // (each wrap line is then centered independently). Long entries stay
    // inside the page margins because splitTextToSize is given the full
    // effective page width.
    for (const rawDesc of lines) {
        const sanitized = sanitizeForHelvetica(rawDesc);
        const wrapped = doc.splitTextToSize(sanitized, effectivePageWidth);
        wrapped.forEach((line) => drawCenteredLine(line));
    }

    yPosition += 4; // small gap after the block
    return yPosition;
}